// A read-only preview of who an approval would address, for the ApproveStrip. The server computes
// the real recipients when it builds the approval snapshot (contract §5); this mirrors that rule
// from the thread the card shows so the reader sees the same people before confirming: reply to
// the sender (or everyone for replyMode "all") minus the sending account, plus any extra Cc or
// forward addresses, which are flagged as new when they are not already in the thread.
import type { Address, ThreadView } from "@shared/api";
import type { CardAction } from "@shared/core";

export interface RecipientPreview {
  to: Address[];
  cc: Address[];
  /** Lower-cased emails that are not already part of the thread. */
  newRecipients: string[];
}

const lower = (email: string) => email.trim().toLowerCase();

function dedupe(list: readonly Address[], exclude: ReadonlySet<string>): Address[] {
  const seen = new Set(exclude);
  const out: Address[] = [];
  for (const a of list) {
    const key = lower(a.email);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

export function threadParticipants(thread: ThreadView | undefined): Set<string> {
  const out = new Set<string>();
  if (!thread) return out;
  for (const p of thread.thread.participants) out.add(lower(p.email));
  for (const m of thread.messages) {
    for (const a of [m.from, ...m.to, ...m.cc, ...m.replyTo]) if (a) out.add(lower(a.email));
  }
  return out;
}

export function previewRecipients(
  thread: ThreadView | undefined,
  accountEmail: string | null,
  action: Pick<CardAction, "actionKind" | "params">,
): RecipientPreview | null {
  const self = new Set(accountEmail ? [lower(accountEmail)] : []);
  const known = threadParticipants(thread);
  const params = action.params ?? {};

  if (action.actionKind === "gmail.forward") {
    const to = dedupe(
      (params.forwardTo ?? []).map((email) => ({ email })),
      self,
    );
    return {
      to,
      cc: [],
      newRecipients: to.map((a) => lower(a.email)).filter((e) => !known.has(e)),
    };
  }
  if (action.actionKind !== "gmail.send_reply") return null;
  if (!thread || !thread.messages.length) {
    const cc = dedupe(
      (params.extraCc ?? []).map((email) => ({ email })),
      self,
    );
    return {
      to: [],
      cc,
      newRecipients: cc.map((a) => lower(a.email)).filter((e) => !known.has(e)),
    };
  }

  const messages = thread.messages;
  const fromOther = [...messages].reverse().find((m) => m.from && !self.has(lower(m.from.email)));
  let to: Address[];
  let cc: Address[] = [];
  if (fromOther?.from) {
    to = fromOther.replyTo.length ? [...fromOther.replyTo] : [fromOther.from];
    if (params.replyMode === "all") cc = [...fromOther.to, ...fromOther.cc];
  } else {
    // Following up on our own message: address the same people again.
    const last = messages[messages.length - 1]!;
    to = [...last.to];
    cc = [...last.cc];
  }
  const toList = dedupe(to, self);
  const toKeys = new Set([...self, ...toList.map((a) => lower(a.email))]);
  const extra = (params.extraCc ?? []).map((email) => ({ email }));
  const ccList = dedupe([...cc, ...extra], toKeys);
  const newRecipients = [...toList, ...ccList]
    .map((a) => lower(a.email))
    .filter((e) => !known.has(e));
  return { to: toList, cc: ccList, newRecipients };
}
